import { useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PreconSectionCard } from "./precon-section-card";
import {
  PreconPhotoLightbox,
  PreconPhotoThumbButton,
  type LightboxPhoto,
} from "./precon-photo-lightbox";
import {
  deriveUnlocatedNumbers,
  isEstablishingShot,
  resolvePlanImage,
} from "./derive-precon-sections";
import type {
  DerivedPhotoEntry,
  DerivedSection,
  DerivedSelection,
  PreconPlanSource,
} from "./derive-precon-sections";
import type { PreconSectionMeta } from "./precon-types";

// Re-exported so existing importers (the step's own test) keep their
// `../precon-sections-step` path; the type's home is now the pure-logic
// derive-precon-sections module that the preview shares.
export type { PreconPlanSource } from "./derive-precon-sections";

interface Props {
  projectId: string;
  derived: DerivedSelection;
  /** Ordered list of section plan_ids (the document's render order). */
  order: string[];
  onOrderChange: (next: string[]) => void;
  /** The project's plans — source of each section sheet's plan image. */
  plans: ReadonlyArray<PreconPlanSource>;
  sectionMeta: Record<string, PreconSectionMeta>;
  onSectionMetaChange: (planId: string, next: PreconSectionMeta) => void;
  /** Per-photo caption overrides, keyed by `image_id`. */
  captions: Record<string, string>;
  onCaptionChange: (imageId: string, value: string) => void;
}

/** Swap two entries in `order`, returning a fresh array (never duplicates). */
function swap(order: string[], i: number, j: number): string[] {
  if (i < 0 || j < 0 || i >= order.length || j >= order.length) return order;
  const next = [...order];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

const blankMeta = (planId: string): PreconSectionMeta => ({
  plan_id: planId,
  precon_date: "",
  level: "",
  grid_lines: "",
  location: "",
  notes: [],
});

/** Trailing no-plan bucket — mirrors the PDF's General Observations appendix. */
function GeneralObservationsGroup({
  unlocated,
  uNumbers,
  captions,
  onCaptionChange,
  onPhotoOpen,
}: {
  unlocated: DerivedPhotoEntry[];
  uNumbers: ReadonlyMap<string, number>;
  captions: Record<string, string>;
  onCaptionChange: (imageId: string, value: string) => void;
  onPhotoOpen: (photo: DerivedPhotoEntry, label: string) => void;
}) {
  if (unlocated.length === 0) return null;
  return (
    <section
      aria-labelledby="general-observations-heading"
      className="space-y-2 rounded-md border border-dashed p-4"
    >
      <h3
        id="general-observations-heading"
        className="text-sm font-semibold"
      >
        General Observations
      </h3>
      <p className="text-xs text-muted-foreground">
        These {unlocated.length} photo
        {unlocated.length === 1 ? " is" : "s are"} on no plan. They will
        appear in a trailing General Observations appendix in the report.
      </p>
      <ul className="space-y-2">
        {unlocated.map((entry) => {
          const uNum = uNumbers.get(entry.record_id) ?? "?";
          const label = `(U${uNum}${entry.subLetter ?? ""})`;
          // Establishing shot seeds from the record remark; the rest start blank.
          const captionValue =
            captions[entry.image_id] ??
            (isEstablishingShot(entry) ? entry.remark : "");
          return (
            <li key={entry.image_id} className="flex items-center gap-3">
              <span className="w-10 shrink-0 text-sm font-medium tabular-nums text-muted-foreground">
                {label}
              </span>
              <PreconPhotoThumbButton
                photo={entry}
                label={label}
                onOpen={onPhotoOpen}
              />
              <Input
                aria-label={`Caption for photo ${label}`}
                value={captionValue}
                onChange={(e) => onCaptionChange(entry.image_id, e.target.value)}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function PreconSectionsStep({
  projectId,
  derived,
  order,
  onOrderChange,
  plans,
  sectionMeta,
  onSectionMetaChange,
  captions,
  onCaptionChange,
}: Props) {
  const { sections, unlocated } = derived;
  const sectionById = new Map<string, DerivedSection>(
    sections.map((s) => [s.plan_id, s]),
  );
  const planById = new Map<string, PreconPlanSource>(
    plans.map((p) => [p.id, p]),
  );

  // Tap-to-enlarge lightbox (US-005). One dialog instance for the whole step;
  // any thumbnail (located, not-located, General Observations) opens it.
  const [lightbox, setLightbox] = useState<LightboxPhoto | null>(null);
  const openPhoto = (photo: DerivedPhotoEntry, label: string) =>
    setLightbox({ photo, label });

  // The document's continuous U# sequence for the CURRENT section order —
  // recomputed on reorder so the labels always state what the PDF would print.
  const uNumbers = useMemo(
    () => deriveUnlocatedNumbers(derived, order),
    [derived, order],
  );

  // US-007 / Empty state: no photo on any plan means no plan-scoped section and
  // the backend can't form a survey. Explain it here — not only on Review — so
  // the user isn't staring at a bare zero-tally with no way forward. Mirrors
  // the Review step's stop; the no-plan photos still list below so it's clear
  // what the selection holds.
  if (sections.length === 0) {
    return (
      <section className="space-y-5">
        <h2 className="text-base font-semibold">Sections</h2>
        <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="flex-1 text-sm">
            A Pre-Condition Survey needs at least one plan with photos. Pin
            some of your selected photos to a plan, then return here — your
            selection and entries are kept.
          </p>
        </div>
        <GeneralObservationsGroup
          unlocated={unlocated}
          uNumbers={uNumbers}
          captions={captions}
          onCaptionChange={onCaptionChange}
          onPhotoOpen={openPhoto}
        />
        <PreconPhotoLightbox
          open={lightbox}
          onClose={() => setLightbox(null)}
        />
      </section>
    );
  }

  return (
    <section className="space-y-5">
      <h2 className="text-base font-semibold">Sections</h2>

      <div className="space-y-4">
        {order.map((planId, i) => {
          const section = sectionById.get(planId);
          if (!section) return null;
          return (
            <PreconSectionCard
              key={planId}
              projectId={projectId}
              section={section}
              meta={sectionMeta[planId] ?? blankMeta(planId)}
              onMetaChange={(next) => onSectionMetaChange(planId, next)}
              captions={captions}
              onCaptionChange={onCaptionChange}
              planImage={resolvePlanImage(planById.get(planId))}
              uNumbers={uNumbers}
              onPhotoOpen={openPhoto}
              onMoveUp={() => onOrderChange(swap(order, i, i - 1))}
              onMoveDown={() => onOrderChange(swap(order, i, i + 1))}
              isFirst={i === 0}
              isLast={i === order.length - 1}
            />
          );
        })}
      </div>

      <GeneralObservationsGroup
        unlocated={unlocated}
        uNumbers={uNumbers}
        captions={captions}
        onCaptionChange={onCaptionChange}
        onPhotoOpen={openPhoto}
      />

      <PreconPhotoLightbox open={lightbox} onClose={() => setLightbox(null)} />
    </section>
  );
}
